// Build the paste-ready UTM query string for the Build View's UTM
// Config section. Pure: takes the resolved render context + the UTM
// config + the platform, returns the literal
// "?utm_source=…&utm_medium=…&utm_campaign=…&utm_term=…&utm_content=…"
// the buyer can paste onto a landing URL.
//
// Behaviour rules from the planning answers:
//   - utm.enabled === false                → null (UI: "No UTM config")
//   - platform not in utm.platforms        → null
//   - platform-specific config missing     → null
//   - every field empty after resolve      → null
//   - otherwise emit a leading "?" so the
//     buyer can paste the string standalone
//
// Each value is passed through renderRowTemplate so per-location
// placeholders ({slug}, {suburbSlug}, …) substitute consistently with
// every other paste-target on the Build View.

import type { UtmConfig } from '../types/campaign.js';
import { renderRowTemplate, type RowRenderContext } from './rowContent.js';

const PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

export function buildUtmQueryString(
  utm: UtmConfig | null | undefined,
  platform: 'meta' | 'google',
  ctx: RowRenderContext,
): string | null {
  if (!utm || !utm.enabled) return null;
  if (!utm.platforms.includes(platform)) return null;
  const config = platform === 'meta' ? utm.meta : utm.google;
  if (!config) return null;

  // The shared campaignName falls back to the shared campaignId so the
  // buyer always has SOMETHING in utm_campaign. Both still pass through
  // renderRowTemplate so author-time placeholders resolve.
  const sharedCampaign =
    utm.shared.campaignName ?? utm.shared.campaignId ?? '';

  const resolved: Record<(typeof PARAM_KEYS)[number], string> = {
    utm_source: renderRowTemplate(config.source, ctx),
    utm_medium: renderRowTemplate(config.medium, ctx),
    utm_campaign: renderRowTemplate(sharedCampaign, ctx),
    utm_term: renderRowTemplate(config.term, ctx),
    utm_content: renderRowTemplate(config.content, ctx),
  };

  // Empty-after-resolve: every field collapsed to '' (e.g. UTM is
  // enabled but the AM never authored values). Surface as null so the
  // UI shows "No UTM config" rather than a literal "?" with no params.
  const anyValue = PARAM_KEYS.some((k) => resolved[k].length > 0);
  if (!anyValue) return null;

  // URL-encode each value. Keep keys literal (they're the well-known
  // utm_* tokens). Drop empty fields so the query string only carries
  // the ones the AM actually authored — matches what buyers expect to
  // paste onto a URL.
  const parts: string[] = [];
  for (const key of PARAM_KEYS) {
    const value = resolved[key];
    if (value.length === 0) continue;
    parts.push(`${key}=${encodeURIComponent(value)}`);
  }
  return `?${parts.join('&')}`;
}
